import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

export default function CustomerTypeStep() {
  const { register, watch, formState: { errors }, setValue, getValues, getFieldState, resetField } = useFormContext();
  const customerType = watch('customerType');
  
  // Keep the submitted loan type aligned with the selected applicant type.
  useEffect(() => {
    if (customerType === 'individual' || customerType === 'company') {
      setValue('loanType', customerType === 'individual' ? 'PERSONAL' : 'BUSINESS');
    }
  }, [customerType, setValue]);

  return (
    <div className="form-section">
      <h6 className="form-section-title">Select Customer Type</h6>
      <p className="text-muted mb-4">
        Choose whether this loan application is for an individual customer or a corporate entity.
      </p>
      
      <div className="row g-3">
        <div className="col-12">
          <div 
            className={`custom-radio ${customerType === 'individual' ? 'selected' : ''}`}
            onClick={() => {
              // Only replace untouched company defaults when starting an individual request.
              // Restored individual drafts and deliberately entered zero values stay intact.
              if (customerType !== 'individual') {
                ['individual.monthlyNetIncome', 'financial.otherLivingExpenses', 'financial.maintenance'].forEach(field => {
                  if (getValues(field) == null) setValue(field, 0);
                });
                ['loan.interestAmount', 'loan.totalRepayment'].forEach(field => {
                  const state = getFieldState(field);
                  if (getValues(field) === 0 && !state.isDirty && !state.isTouched) {
                    setValue(field, undefined);
                  }
                });
                ['loan.loanAmount', 'loan.interestRate', 'loan.adminFee'].forEach(field => {
                  const state = getFieldState(field);
                  if (getValues(field) === 0 && !state.isDirty && !state.isTouched) {
                    register(field);
                    resetField(field, { defaultValue: '' });
                  }
                });
              }
              setValue('customerType', 'individual', {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
            }}
          >
            <div className="form-check">
              <input
                type="radio"
                className="form-check-input"
                id="individual"
                value="individual"
                {...register('customerType', { required: 'Please select a customer type' })}
              />
              <label className="form-check-label" htmlFor="individual">
                <div className="ms-2">
                  <h6 className="mb-1">
                    <i className="bi bi-person me-2"></i>
                    Individual Customer
                  </h6>
                  <p className="text-muted small mb-0">
                    Personal loan application for individual customers
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div 
            className={`custom-radio ${customerType === 'company' ? 'selected' : ''}`}
            onClick={() => {
              setValue('customerType', 'company', {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
            }}
          >
            <div className="form-check">
              <input
                type="radio"
                className="form-check-input"
                id="company"
                value="company"
                {...register('customerType', { required: 'Please select a customer type' })}
              />
              <label className="form-check-label" htmlFor="company">
                <div className="ms-2">
                  <h6 className="mb-1">
                    <i className="bi bi-building me-2"></i>
                    Corporate Customer
                  </h6>
                  <p className="text-muted small mb-0">
                    Business loan application for corporate clients and companies
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {errors.customerType && (
          <div className="col-12">
            <div className="alert alert-danger">
              {errors.customerType.message as string}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
